import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shield, CheckCircle2, Clock, Loader2, XCircle, QrCode } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_META = {
  new: { label: "Submitted", icon: Clock, color: "text-sage" },
  in_review: { label: "In Review", icon: Loader2, color: "text-warning" },
  approved: { label: "Approved", icon: CheckCircle2, color: "text-success" },
  rejected: { label: "Decision: Not approved", icon: XCircle, color: "text-danger" },
};

const STAGES = ["new", "in_review", "approved"];

export default function ClientTracker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState(null);

  useEffect(() => {
    api.get(`/track/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This tracking link is invalid or has expired."))
      .finally(() => setLoading(false));
    api.get(`/track/${token}/qr`)
      .then((r) => setQr(r.data.data_url))
      .catch(() => {});
  }, [token]);

  return (
    <div className="min-h-screen bg-bone">
      <header className="border-b border-line bg-bone">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="tracker-brand-link">
            <div className="w-8 h-8 bg-forest rounded-sm flex items-center justify-center">
              <Shield className="w-4 h-4 text-bone" strokeWidth={2} />
            </div>
            <span className="font-heading font-medium text-lg tracking-tight">InsureWell</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12 md:py-16">
        {loading && <div className="text-sage" data-testid="tracker-loading">Loading…</div>}

        {error && (
          <div className="bg-white border border-line rounded-sm p-10 text-center" data-testid="tracker-error">
            <XCircle className="w-10 h-10 text-danger mx-auto mb-4" strokeWidth={1.4} />
            <h1 className="font-heading text-2xl mb-2">Tracker not found</h1>
            <p className="text-sage">{error}</p>
          </div>
        )}

        {data && (
          <div data-testid="tracker-content">
            <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-3">Application status</div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-2">
              Hi {data.full_name.split(" ")[0]} — here's where you are.
            </h1>
            <p className="text-sage mb-10">
              {data.insurance_type === "health" ? "Health" : "Life"} insurance application submitted on{" "}
              {new Date(data.submitted_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.
            </p>

            <div className="bg-white border border-line rounded-sm p-8 md:p-10 mb-6">
              {/* Progress stages */}
              {data.status !== "rejected" ? (
                <ol className="relative" data-testid="tracker-progress">
                  {STAGES.map((stage, idx) => {
                    const meta = STATUS_META[stage];
                    const currentIdx = STAGES.indexOf(data.status);
                    const isActive = idx === currentIdx;
                    const isDone = idx < currentIdx;
                    return (
                      <li key={stage} className="flex items-start gap-4 pb-8 last:pb-0 relative">
                        {idx < STAGES.length - 1 && (
                          <span
                            className={`absolute left-[15px] top-9 bottom-0 w-px ${
                              isDone ? "bg-success" : "bg-line"
                            }`}
                          />
                        )}
                        <span
                          className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isDone ? "bg-success border-success" : isActive ? "bg-clay border-clay" : "bg-bone border-line"
                          }`}
                        >
                          <meta.icon
                            className={`w-4 h-4 ${
                              isDone || isActive ? "text-bone" : "text-sage"
                            } ${isActive && stage === "in_review" ? "animate-spin" : ""}`}
                            strokeWidth={2}
                          />
                        </span>
                        <div className="pt-1">
                          <div className={`font-heading text-lg ${isActive ? "text-forest" : isDone ? "text-forest" : "text-sage"}`}>
                            {meta.label}
                          </div>
                          {isActive && (
                            <p className="text-sm text-sage mt-1">
                              {stage === "new" && "We've received your application and assigned it to an agent."}
                              {stage === "in_review" && "Your agent is reviewing your information and documents."}
                              {stage === "approved" && "Congratulations! Your application has been approved. We'll be in touch with next steps."}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="text-center py-6" data-testid="tracker-rejected">
                  <XCircle className="w-10 h-10 text-danger mx-auto mb-4" strokeWidth={1.4} />
                  <div className="font-heading text-xl mb-2">Application not approved</div>
                  <p className="text-sage">Your agent will reach out with details and next steps.</p>
                </div>
              )}
            </div>

            {qr && (
              <div className="bg-white border border-line rounded-sm p-6 mb-6 flex items-start gap-5" data-testid="tracker-qr-card">
                <img
                  src={qr}
                  alt="Tracker QR"
                  className="w-24 h-24 bg-white p-2 border border-line rounded-sm flex-shrink-0"
                  data-testid="tracker-qr-image"
                />
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-clay font-semibold mb-2">
                    <QrCode className="w-3.5 h-3.5" /> Quick access
                  </div>
                  <p className="text-sm text-forest">
                    Scan this code from your phone — bookmark, screenshot or share it with family. It always points to the latest status of this application.
                  </p>
                </div>
              </div>
            )}

            {data.history?.length > 0 && (
              <div className="bg-white border border-line rounded-sm p-6">
                <div className="text-xs uppercase tracking-[0.15em] text-sage font-medium mb-4">History</div>
                <ul className="divide-y divide-line text-sm">
                  {data.history.map((h, i) => (
                    <li key={i} className="py-2 flex justify-between" data-testid={`history-row-${i}`}>
                      <span className="text-forest">
                        Status changed: <span className="capitalize">{h.from?.replace("_", " ")}</span> →{" "}
                        <span className="capitalize font-medium">{h.to?.replace("_", " ")}</span>
                      </span>
                      <span className="text-sage">{new Date(h.at).toLocaleDateString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
